import {
  CreditLineCosts,
  CreditLineAmountSelector,
  CreditLineDetails,
  CreditLineInfo,
  DerogationWarning,
} from '@/features/application/components';
import styles from './CreditLineCalculatorPage.module.scss';
import {
  CREDIT_LINE_MIN_PAYMENT_PERCENTAGE,
  useLoanOffer,
} from '@/features/offer';
import {
  calculateCreditLineOfferDetails,
  calculateCreditLinePaymentExample,
} from '@/features/offer/hooks/loanOfferUtils';
import type { CreditOffer } from '@/features/offer/types';
import { useApplication } from '../../hooks/useApplication';

interface CreditLineCalculatorPageProps {
  // The preliminary offer; the final one by default.
  offer?: CreditOffer;
}

export function CreditLineCalculatorPage({ offer }: CreditLineCalculatorPageProps) {
  const { creditLineOffer: finalCreditLineOffer } = useLoanOffer(true);
  const creditLineOffer = offer ?? finalCreditLineOffer;
  const { creditLineDraft, setCreditLineAmount } = useApplication();

  // The draft is shared by the preliminary and the final calculator, whose
  // limits can differ.
  const minAmount = creditLineOffer?.minAmount ?? 0;
  const selectedAmount = Math.min(
    Math.max(creditLineDraft?.amount ?? minAmount, minAmount),
    creditLineOffer?.creditLimit ?? minAmount
  );

  const paymentExample = calculateCreditLinePaymentExample(
    creditLineOffer?.interestDailyPercentage ?? 0,
    selectedAmount
  );

  const details = calculateCreditLineOfferDetails(
    creditLineOffer,
    creditLineDraft,
    selectedAmount
  );

  const handleAmountChange = (amount: number) => {
    setCreditLineAmount(amount);
  };

  return (
    <div className={styles.calculatorContent}>
      {/* Cost Card */}
      <CreditLineCosts
        monthlyCost={paymentExample.monthlyCostExample}
        minimumPayment={paymentExample.minPaymentExample}
      />

      {/* Amount Card */}
      <CreditLineAmountSelector
        creditAmount={selectedAmount}
        minAmount={creditLineOffer?.minAmount ?? 0}
        maxAmount={creditLineOffer?.creditLimit ?? 0}
        dailyInterest={paymentExample.interestExample}
        onAmountChange={handleAmountChange}
      />

      {details?.isDerogation && <DerogationWarning />}

      {/* Details Section */}
      <CreditLineDetails
        creditAmount={creditLineOffer?.creditLimit ?? 0}
        contractDuration={creditLineOffer?.numberOfRates ?? 0}
      />

      {/* Info Section */}
      <CreditLineInfo
        minPaymentPercentage={CREDIT_LINE_MIN_PAYMENT_PERCENTAGE}
      />
    </div>
  );
}
